import classNames from 'classnames';
import { useMemo } from 'react';

import { FCWithOptionalChild } from '../../interfaces/Shared';
import { useFormatting } from '../../services/hooks/useFormatting';
import Button from '../Button';
import MaterialIcon, { IconNames } from '../MaterialIcon/MaterialIcon';
import style from './Alert.module.scss';

interface AlertProps {
  buttonStyle?: 'button' | 'text';
  className?: string;
  icon?: IconNames;
  level?: 'error' | 'info' | 'success' | 'warn';
  subtext?: string;
  text: string;
  textButton?: { onClick: () => void; text: string };
  white?: true;
}

const Alert: FCWithOptionalChild<AlertProps> = ({
  text, subtext, level, icon = 'info', textButton, children, white, className, buttonStyle
}) => {
  const { formattedText: alertText } = useFormatting(text);
  const { formattedText: alertSubtext } = useFormatting(subtext ?? '');

  const alertClassNames = classNames([
    style.container,
    style[`${level}Background`],
    white && style.whiteBg,
    level === 'info' && style.withShadow,
    className,
  ]);

  const iconClassNames = classNames(
    style.icon,
    level ? style[`${level}Icon`] : '');

  const renderedButton = useMemo(() => {
    if (!textButton) return null;

    if (buttonStyle === 'button') {
      return <Button
        onClick={textButton.onClick}
        size="small"
        text={textButton.text}
        variant="outline"
      />;
    } else {
      return (
        <Button
          ariaLabel={textButton.text}
          className={style.alertTextButton}
          onClick={textButton.onClick}
          text={textButton.text}
          variant="text"
        />
      );
    }
  }, [textButton, buttonStyle]);

  return (
    <div className={alertClassNames}>
      <div className={classNames(style.verticalLine, style[`${level}VerticalLine`])}/>
      {!!icon && <MaterialIcon className={iconClassNames} name={icon} />}
      <div className={style.content}>
        {!!alertText && <span className={style.alertText} dangerouslySetInnerHTML={{ __html: alertText }} />}
        {!!alertSubtext && <span className={style.alertSubtext} dangerouslySetInnerHTML={{ __html: alertSubtext }} />}
        {renderedButton}
        {children}
      </div>
    </div>
  );
};

export default Alert;
